import React, { useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { GALLERY_GROUPS } from '../data/mock';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { cdnImg } from '../utils/cdn';

const Gallery = () => {
  const [openGroup, setOpenGroup] = useState(null);
  const [activeImage, setActiveImage] = useState(null);

  return (
    <div>
      <PageHeader title="Gallery" breadcrumbs={[{ label: 'Gallery' }]} />

      <section className="py-16 lg:py-20">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10">
          {!openGroup ? (
            <>
              <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
                Real photos from Ichi &amp; Ori Pet Care. Tap a category to browse.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {GALLERY_GROUPS.map((group) => (
                  <button
                    key={group.title}
                    onClick={() => setOpenGroup(group)}
                    className="group relative overflow-hidden rounded-2xl aspect-[4/3] shadow-md hover:shadow-2xl transition-all hover:-translate-y-1 bg-slate-100 text-left"
                  >
                    {group.images[0] && (
                      <img
                        src={cdnImg(group.images[0], { w: 800 })}
                        alt={group.title}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a0b3d]/85 via-[#1a0b3d]/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-serif text-[22px] lg:text-[26px] font-extrabold text-white mb-1">
                        {group.title}
                      </h3>
                      <p className="text-white/80 text-[13px]">
                        {group.images.length} photo{group.images.length !== 1 ? 's' : ''} &mdash; tap to view
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => setOpenGroup(null)}
                  className="rounded-full border-[#5b3eb5] text-[#5b3eb5] hover:bg-[#5b3eb5] hover:text-white gap-2"
                >
                  <ChevronLeft size={16} /> All Categories
                </Button>
                <h2 className="font-serif text-[28px] lg:text-[36px] font-extrabold text-[#1a0b3d]">
                  {openGroup.title}
                </h2>
                <span className="text-slate-500 text-[14px]">
                  {openGroup.images.length} photo{openGroup.images.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {openGroup.images.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(src)}
                    className="group relative overflow-hidden rounded-xl aspect-square shadow-sm hover:shadow-xl transition-all"
                  >
                    <img
                      src={cdnImg(src, { w: 600 })}
                      alt={`${openGroup.title} ${idx + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-[#1a0b3d]/0 group-hover:bg-[#1a0b3d]/30 transition-colors" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Dialog open={!!activeImage} onOpenChange={(o) => !o && setActiveImage(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden border-0 bg-transparent shadow-none">
          {activeImage && (
            <div className="relative">
              <button
                onClick={() => setActiveImage(null)}
                className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-white text-[#1a0b3d] shadow-lg flex items-center justify-center hover:bg-[#f3d6f5] transition-colors z-10"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <img src={cdnImg(activeImage, { w: 1600, q: 85 })} alt="Preview" className="w-full h-auto rounded-2xl" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
